"use client"

import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { motion } from 'framer-motion'
import { BarChart, Bar, XAxis, CartesianGrid, PieChart, Pie, Cell } from 'recharts'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import type { RootState, AppDispatch } from '../../store'
import { fetchColleges } from '../../store/slices/collegeSlice'
import { fetchStudents } from '../../store/slices/studentSlice'
import { fetchFeePayments } from '../../store/slices/feeSlice'

const Analytics = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { colleges } = useSelector((state: RootState) => state.college)
  const { students } = useSelector((state: RootState) => state.student)
  const { payments } = useSelector((state: RootState) => state.fee)

  const [collegeAnalytics, setCollegeAnalytics] = useState<any[]>([])
  const [revenueData, setRevenueData] = useState<any[]>([])

  // Chart configurations
  const studentsChartConfig = {
    students: {
      label: "Students",
      color: "hsl(var(--chart-1))",
    },
  } satisfies ChartConfig

  const revenueChartConfig = {
    revenue: {
      label: "Revenue",
      color: "hsl(var(--chart-2))",
    },
  } satisfies ChartConfig

  const pieChartConfig = {
    revenue: {
      label: "Revenue",
      color: "hsl(var(--chart-3))",
    },
  } satisfies ChartConfig

  useEffect(() => {
    dispatch(fetchColleges({ page: 1, limit: 100 }))
    dispatch(fetchStudents({ page: 1, limit: 100 }))
    dispatch(fetchFeePayments({ page: 1, limit: 100 }))
  }, [dispatch])

  useEffect(() => {
    if (colleges.length > 0 && students.length > 0) {
      // Process analytics data
      const analytics = colleges.map(college => {
        const collegeStudents = students.filter(student => student.college?.name === college.name)
        const collegeRevenue = payments
          .filter(payment => collegeStudents.some(student => student.rollNumber === payment.student?.rollNumber))
          .reduce((sum, payment) => sum + Number(payment.amountPaid), 0)

        return {
          name: college.name,
          code: college.code,
          students: collegeStudents.length,
          revenue: collegeRevenue,
          established: college.establishedYear
        }
      })

      setCollegeAnalytics(analytics)

      // Prepare revenue data for charts
      const revenueByMonth = payments.reduce((acc, payment) => {
        const month = new Date(payment.paymentDate).toLocaleDateString('en-IN', { month: 'short' })
        const existing = acc.find(item => item.month === month)
        if (existing) {
          existing.revenue += Number(payment.amountPaid)
        } else {
          acc.push({ month, revenue: Number(payment.amountPaid) })
        }
        return acc
      }, [] as any[])

      setRevenueData(revenueByMonth)

      console.log('analytics')
      console.log(analytics)

      console.log('revenueByMonth')
      console.log(revenueByMonth)
    }
  }, [colleges, students, payments])

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316']

  return (
    <div className="space-y-6">
      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* College-wise Students Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Students by College</CardTitle>
              <CardDescription>Distribution of students across institutions</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={studentsChartConfig}>
                <BarChart accessibilityLayer data={collegeAnalytics}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="code"
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    tickFormatter={(value) => value.slice(0, 5)}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload
                        return (
                          <div className="rounded-lg border bg-background p-2 shadow-md">
                            <div className="grid gap-2">
                              <div className="font-medium">{data.name}</div>
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-2 bg-black"></div>
                                <span className="text-sm text-muted-foreground">Students:</span>
                                <span className="font-medium">{data.students}</span>
                              </div>
                            </div>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Bar dataKey="students" fill="#000" radius={8} />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Revenue Distribution Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Revenue by College</CardTitle>
              <CardDescription>Fee collection distribution</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={pieChartConfig} className="mx-auto aspect-square max-h-[250px]">
                <PieChart>
                  <ChartTooltip
                    cursor={false}
                    content={<ChartTooltipContent hideLabel />}
                  />
                  <Pie
                    data={collegeAnalytics}
                    dataKey="revenue"
                    nameKey="code"
                    innerRadius={60}
                    strokeWidth={5}
                  >
                    {collegeAnalytics.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Monthly Revenue Trend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Revenue Trend</CardTitle>
            <CardDescription>Monthly fee collection overview</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={revenueChartConfig}>
              <BarChart accessibilityLayer data={revenueData}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="month"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value) => value.slice(0, 3)}
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent hideLabel />}
                />
                <Bar dataKey="revenue" fill="var(--color-revenue)" radius={8} />
              </BarChart>
            </ChartContainer>
            </CardContent>
          </Card>
      </motion.div>
    </div>
  )
}

export default Analytics
